// src/testMode/steps.js
// One function per manual step of a tournament. Each talks to the backend, generates any
// irrelevant data randomly, and returns a short message describing what it did (or throws).
import { apiRequest as request } from '../api';
import { pick, randomPlayerName, randomSetScore } from './randomData';

export const MIN_PLAYERS = 4; // one matchup = two teams of two
export const N_SETS = 3; // sets per match, mirrors Round.n_sets in backend/entities.py
const SETS_TO_WIN = Math.floor(N_SETS / 2) + 1;

// ---------------------------------------------------------------------------------------------
// Backend access
// ---------------------------------------------------------------------------------------------
export const getTournaments = (userId) => request('GET', '/getTournaments', userId);

export async function getTournamentState({ userId, tournamentId }) {
    const [players, rounds] = await Promise.all([
        request('GET', `/tournaments/${tournamentId}/players`, userId),
        request('GET', `/tournaments/${tournamentId}/rounds`, userId),
    ]);
    return { players, rounds };
}

// ---------------------------------------------------------------------------------------------
// Reading the tournament state (pure)
// ---------------------------------------------------------------------------------------------
const latestRound = (rounds) => rounds[rounds.length - 1];
const matchesOf = (round) => round.matchups.flatMap((matchup) => matchup.matches.map((match) => ({ matchup, match })));
const isUnscored = (set) => set.team1Score === 0 && set.team2Score === 0;

function isDecided(match) {
    const wins = { team1: 0, team2: 0 };
    for (const set of match.sets) {
        if (isUnscored(set)) continue;
        if (set.team1Score > set.team2Score) wins.team1 += 1;
        if (set.team2Score > set.team1Score) wins.team2 += 1;
    }
    return Math.max(wins.team1, wins.team2) >= SETS_TO_WIN;
}

const findMatchupWithoutMatch = (round) => round.matchups.find((matchup) => matchup.matches.length === 0);

function findUnscoredSet(round) {
    for (const { matchup, match } of matchesOf(round)) {
        const set = match.sets.find(isUnscored);
        if (set) return { matchup, match, set };
    }
    return null;
}

// The undecided match with the fewest sets, as long as it can still take another one.
function findMatchNeedingSet(round) {
    const candidates = matchesOf(round).filter(({ match }) => match.sets.length < N_SETS && !isDecided(match));
    candidates.sort((a, b) => a.match.sets.length - b.match.sets.length);
    return candidates[0] || null;
}

const isActive = (player) => player.active !== false;

// Which step comes next when playing the tournament through: fill up the players, create a round,
// add a match per matchup, score the sets, add another set until each match has a winner, repeat.
export function chooseNextStep({ players, rounds }) {
    if (players.filter(isActive).length < MIN_PLAYERS) return 'addPlayer';
    const round = latestRound(rounds);
    if (!round) return 'addRound';
    if (findMatchupWithoutMatch(round)) return 'addMatch';
    if (findUnscoredSet(round)) return 'recordScore';
    if (findMatchNeedingSet(round)) return 'addSet';
    return 'addRound';
}

// ---------------------------------------------------------------------------------------------
// Tournament steps.  ctx = { userId, tournamentId }
// ---------------------------------------------------------------------------------------------
export async function addPlayer({ userId, tournamentId }) {
    const name = randomPlayerName();
    await request('POST', `/tournaments/${tournamentId}/players`, userId, name);
    return `Added player ${name.firstName} ${name.lastName}`;
}

export async function addPlayers(ctx, count) {
    for (let i = 0; i < count; i += 1) {
        await addPlayer(ctx);
    }
    return `Added ${count} random players`;
}

export async function addRound({ userId, tournamentId }) {
    const round = await request('POST', `/tournaments/${tournamentId}/rounds`, userId);
    return `Created round ${round.roundId + 1} with ${round.matchups.length} matchup(s)`;
}

export async function addMatch(ctx) {
    const { userId, tournamentId } = ctx;
    const round = latestRound((await getTournamentState(ctx)).rounds);
    if (!round) throw new Error('There is no round yet');
    const matchup = findMatchupWithoutMatch(round);
    if (!matchup) throw new Error('Every matchup of the latest round already has a match');
    const match = await request(
        'POST',
        `/tournaments/${tournamentId}/rounds/${round.roundId}/matchups/${matchup.matchupId}/matches`,
        userId
    );
    return `Added match ${match.matchId + 1} to matchup ${round.matchups.indexOf(matchup) + 1} (round ${round.roundId + 1})`;
}

export async function addSet(ctx) {
    const { userId, tournamentId } = ctx;
    const round = latestRound((await getTournamentState(ctx)).rounds);
    if (!round) throw new Error('There is no round yet');
    const target = findMatchNeedingSet(round);
    if (!target) throw new Error('No match in the latest round needs another set');
    const { matchup, match } = target;
    const set = await request(
        'POST',
        `/tournaments/${tournamentId}/rounds/${round.roundId}/matchups/${matchup.matchupId}/matches/${match.matchId}/sets`,
        userId
    );
    return `Added set ${set.setId + 1} to match ${match.matchId + 1} (round ${round.roundId + 1})`;
}

export async function recordScore(ctx) {
    const { userId, tournamentId } = ctx;
    const round = latestRound((await getTournamentState(ctx)).rounds);
    if (!round) throw new Error('There is no round yet');
    const target = findUnscoredSet(round);
    if (!target) throw new Error('No unscored set in the latest round');
    const { matchup, match, set } = target;
    const score = randomSetScore();
    await request(
        'PATCH',
        `/tournaments/${tournamentId}/rounds/${round.roundId}/matchups/${matchup.matchupId}/matches/${match.matchId}/sets/${set.setId}`,
        userId,
        score
    );
    return `Set ${set.setId + 1} of match ${match.matchId + 1}: ${score.team1} - ${score.team2}`;
}

export async function nextStep(ctx) {
    const state = await getTournamentState(ctx);
    const stepId = chooseNextStep(state);
    const message = await TOURNAMENT_STEP_RUNNERS[stepId](ctx);
    return `${message} (auto: ${stepId})`;
}

// ---- active / inactive ----------------------------------------------------------------------
export async function deactivateRandomPlayer(ctx) {
    const { players } = await getTournamentState(ctx);
    const candidates = players.filter(isActive);
    if (candidates.length === 0) throw new Error('There are no active players');
    const player = pick(candidates);
    await request('PATCH', `/tournaments/${ctx.tournamentId}/players/${player.playerId}`, ctx.userId, { active: false });
    return `Deactivated ${player.firstName} ${player.lastName}`;
}

export async function activateAPlayer(ctx) {
    const { players } = await getTournamentState(ctx);
    const player = players.find((candidate) => !isActive(candidate));
    if (!player) throw new Error('There are no inactive players');
    await request('PATCH', `/tournaments/${ctx.tournamentId}/players/${player.playerId}`, ctx.userId, { active: true });
    return `Activated ${player.firstName} ${player.lastName}`;
}

// ---- deleting -------------------------------------------------------------------------------
export async function deleteLastPlayer(ctx) {
    const { players } = await getTournamentState(ctx);
    const player = players[players.length - 1];
    if (!player) throw new Error('There are no players');
    await request('DELETE', `/tournaments/${ctx.tournamentId}/players/${player.playerId}`, ctx.userId);
    return `Deleted player ${player.firstName} ${player.lastName}`;
}

export async function deleteLastRound(ctx) {
    const round = latestRound((await getTournamentState(ctx)).rounds);
    if (!round) throw new Error('There are no rounds');
    await request('DELETE', `/tournaments/${ctx.tournamentId}/rounds/${round.roundId}`, ctx.userId);
    return `Deleted round ${round.roundId + 1}`;
}

export async function deleteLastMatch(ctx) {
    const round = latestRound((await getTournamentState(ctx)).rounds);
    const targets = round ? matchesOf(round) : [];
    const target = targets[targets.length - 1];
    if (!target) throw new Error('There are no matches in the latest round');
    await request(
        'DELETE',
        `/tournaments/${ctx.tournamentId}/rounds/${round.roundId}/matchups/${target.matchup.matchupId}/matches/${target.match.matchId}`,
        ctx.userId
    );
    return `Deleted match ${target.match.matchId + 1}`;
}

export async function deleteLastSet(ctx) {
    const round = latestRound((await getTournamentState(ctx)).rounds);
    const targets = round ? matchesOf(round).filter(({ match }) => match.sets.length > 0) : [];
    const target = targets[targets.length - 1];
    if (!target) throw new Error('There are no sets in the latest round');
    const set = target.match.sets[target.match.sets.length - 1];
    await request(
        'DELETE',
        `/tournaments/${ctx.tournamentId}/rounds/${round.roundId}/matchups/${target.matchup.matchupId}/matches/${target.match.matchId}/sets/${set.setId}`,
        ctx.userId
    );
    return `Deleted set ${set.setId + 1} of match ${target.match.matchId + 1}`;
}

const TOURNAMENT_STEP_RUNNERS = {
    addPlayer,
    addRound,
    addMatch,
    addSet,
    recordScore,
};

// ---------------------------------------------------------------------------------------------
// Dashboard steps.  ctx = { userId }
// ---------------------------------------------------------------------------------------------
export async function createTournament({ userId }) {
    const tournament = await request('POST', '/createTournament', userId);
    return `Created ${tournament.name} (id ${tournament.tournamentId})`;
}

export async function deleteLastTournament({ userId }) {
    const tournaments = await getTournaments(userId);
    const tournament = tournaments[tournaments.length - 1];
    if (!tournament) throw new Error('There are no tournaments');
    await request('DELETE', '/deleteTournament', userId, { tournamentId: tournament.tournamentId });
    return `Deleted ${tournament.name} (id ${tournament.tournamentId})`;
}

// What the panel renders, in order. `run(ctx)` returns the message to show.
export const TOURNAMENT_STEPS = [
    { id: 'nextStep', label: 'Next step (auto)', run: nextStep },
    { id: 'addPlayer', label: 'Add random player', run: addPlayer },
    { id: 'addRound', label: 'Create round', run: addRound },
    { id: 'addMatch', label: 'Add match', run: addMatch },
    { id: 'addSet', label: 'Add set', run: addSet },
    { id: 'recordScore', label: 'Record random score', run: recordScore },
    { id: 'deactivatePlayer', label: 'Deactivate random player', run: deactivateRandomPlayer },
    { id: 'activatePlayer', label: 'Activate a player', run: activateAPlayer },
    { id: 'deleteLastSet', label: 'Delete last set', run: deleteLastSet },
    { id: 'deleteLastMatch', label: 'Delete last match', run: deleteLastMatch },
    { id: 'deleteLastRound', label: 'Delete last round', run: deleteLastRound },
    { id: 'deleteLastPlayer', label: 'Delete last player', run: deleteLastPlayer },
];

export const DASHBOARD_STEPS = [
    { id: 'createTournament', label: 'Create tournament', run: createTournament },
    { id: 'deleteLastTournament', label: 'Delete last tournament', run: deleteLastTournament },
];
